import {
  format,
  isValid,
  parse,
  startOfMonth,
} from "date-fns"
import { ptBR } from "date-fns/locale"
import { CalendarDays } from "lucide-react"
import React from "react"
import { withMask } from "use-mask-input"

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

import AppCalendar from "./app-calendar"
interface DateInputProps {
  selected?: Date
  onSelect: (value?: Date) => void
  placeholder?: string
  isOnlyPast?: boolean
  fromDate?: Date
  toDate?: Date
  disabledField?: boolean
}
export const AppDateInput: React.FC<DateInputProps> = ({
  onSelect,
  selected,
  placeholder,
  isOnlyPast = false,
  fromDate,
  toDate,
  disabledField = false,
}) => {
  const inputId = React.useId()
  const [inputValue, setInputValue] = React.useState(
    selected
      ? format(selected, "dd/MM/yyyy", { locale: ptBR })
      : ""
  )
  const [prevSelected, setPrevSelected] =
    React.useState(selected)
  const [month, setMonth] = React.useState(
    selected || new Date()
  )
  const disabled = isOnlyPast
    ? (date: Date) => date > new Date()
    : undefined
  if (prevSelected !== selected) {
    setPrevSelected(selected)
    setInputValue(
      selected
        ? format(selected, "dd/MM/yyyy", { locale: ptBR })
        : ""
    )
    if (selected) {
      const selectedMonth = startOfMonth(selected)
      const currentMonth = startOfMonth(month)
      if (
        selectedMonth.getTime() !== currentMonth.getTime()
      ) {
        setMonth(selected)
      }
    }
  }
  const handleSelectDate = (date?: Date) => {
    if (!date) {
      setInputValue("")
      onSelect(undefined)
    } else {
      setInputValue(
        format(date, "dd/MM/yyyy", { locale: ptBR })
      )
      onSelect(date)
    }
  }
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setInputValue(event.target.value)
    const parsedDate = parse(
      event.target.value,
      "dd/MM/yyyy",
      new Date(),
      {
        locale: ptBR,
      }
    )
    if (isValid(parsedDate)) {
      onSelect(parsedDate)
    } else {
      onSelect(undefined)
    }
  }
  return (
    <Popover>
      <div
        className={
          "flex h-10 w-full rounded-md border border-input bg-background py-1 pl-3 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 has-[:focus]:outline-none has-[:focus]:ring-1 has-[:focus]:ring-ring"
        }
      >
        <input
          ref={(input) => {
            if (input) {
              withMask("99/99/9999", {
                showMaskOnFocus: false,
                showMaskOnHover: false,
                inputFormat: "dd-mm-yyyy",
              })(input)
            }
          }}
          id={inputId}
          type="text"
          className="h-full w-full bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
          value={inputValue ?? ""}
          placeholder={placeholder}
          onChange={handleInputChange}
          disabled={disabledField}
        />
        <PopoverTrigger
          disabled={disabledField}
          className="group items-center justify-center px-3 disabled:opacity-50"
        >
          <CalendarDays className="shrink-0 group-disabled:cursor-not-allowed" />
        </PopoverTrigger>
      </div>
      <PopoverContent align="end" className="w-auto p-0">
        <AppCalendar
          mode="single"
          captionLayout="dropdown-buttons"
          selected={selected}
          onSelect={handleSelectDate}
          month={month}
          onMonthChange={setMonth}
          disabled={disabled}
          locale={ptBR}
          fromDate={fromDate}
          toDate={toDate}
        />
      </PopoverContent>
    </Popover>
  )
}
export default AppDateInput
